import React, { useState } from "react";
import type { ProjectSnapshot } from "../services/projectService";

interface ProjectPanelProps {
  projectId: string | null;
  snapshots: ProjectSnapshot[];
  onLoad: (id: string) => void;
  onSelectSnapshot: (snapshot: ProjectSnapshot) => void;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ProjectPanel({
  projectId,
  snapshots,
  onLoad,
  onSelectSnapshot,
}: ProjectPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loadInput, setLoadInput] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!projectId) return;
    navigator.clipboard.writeText(projectId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleLoad = () => {
    const id = loadInput.trim();
    if (!id) return;
    onLoad(id);
    setLoadInput("");
  };

  return (
    <div className="mx-6 mb-4 rounded-lg border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-widest hover:text-slate-200 hover:bg-white/5 transition-colors"
      >
        <span>Project History</span>
        <span className="text-slate-600">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="border-t border-white/10 p-4 space-y-4">
          {/* Project ID display */}
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Current project</p>
            {projectId ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-cyan-300 bg-cyan-950/40 border border-cyan-500/20 rounded px-2 py-1 truncate">
                  {projectId}
                </code>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded border border-white/10 hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-600 italic">No project saved yet — start a conversation to auto-save.</p>
            )}
          </div>

          {/* Load by ID */}
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Load project by ID</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={loadInput}
                onChange={(e) => setLoadInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLoad()}
                placeholder="proj_..."
                className="flex-1 text-xs font-mono bg-black/30 border border-white/10 rounded px-2 py-1.5 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40"
              />
              <button
                type="button"
                onClick={handleLoad}
                disabled={!loadInput.trim()}
                className="text-xs px-3 py-1.5 rounded border border-cyan-500/30 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Load
              </button>
            </div>
          </div>

          {/* Snapshot history */}
          {snapshots.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Version history</p>
              <ul className="space-y-1">
                {[...snapshots].reverse().map((snap) => (
                  <li key={snap.version}>
                    <button
                      type="button"
                      onClick={() => onSelectSnapshot(snap)}
                      className="w-full text-left text-xs px-2.5 py-1.5 rounded border border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/15 transition-colors"
                    >
                      <span className="text-cyan-400 font-mono font-semibold">V{snap.version}</span>
                      <span className="text-slate-400 mx-1.5">—</span>
                      <span className="text-slate-300">{snap.agent_leg}</span>
                      <span className="text-slate-600 ml-1.5">{fmtDate(snap.timestamp)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
